import {Operation, NextLink, Observer} from "@apollo/client";
import {TStore} from "./types";
import {setBackendError} from "../actions";
import {UserManager} from "oidc-client-ts";

type Request = {
    operation: Operation;
    forward: NextLink;
    observer: Observer<any>;
};

export default class ExpiredAccessTokenHandler {
    private storedRequests: Request[];
    private renewInProgress: boolean;
    private store: TStore;

    constructor(store: TStore) {
        this.renewInProgress = false;
        this.storedRequests = [];
        this.store = store;
    }

    async handleRequest(request: Request, userManager: UserManager): Promise<void> {
        if (this.renewInProgress) {
            console.debug("token renew in progress, store %s operation", request.operation.operationName);
            this.storedRequests.push(request);
            return;
        }

        this.renewInProgress = true;

        console.debug("start refreshing the access_token...");

        try {
            const user = await userManager.signinSilent();
            if (!user || !user.access_token) {
                console.warn("signinSilent did not return a valid user, reloading the page");
                // Clear stored tokens before reloading to prevent stale token issues
                await userManager.removeUser();
                window.location.reload();
                return;
            }

            this.renewInProgress = false;

            const requests: Request[] = [request, ...this.storedRequests];
            this.storedRequests = [];

            for (const req of requests) {
                const subscriber = {
                    next: req.observer.next?.bind(req.observer),
                    error: req.observer.error?.bind(req.observer),
                    complete: req.observer.complete?.bind(req.observer),
                };

                req.operation.setContext(({headers}: {headers?: Record<string, string>}) => ({
                    headers: {
                        ...headers,
                        authorization: `Bearer ${user.access_token}`,
                    },
                }));
                req.forward(req.operation).subscribe(subscriber);
            }
        } catch (error) {
            console.warn(`error with refreshing the access_token: ${JSON.stringify(error)}`);
            if (error instanceof Error && error.message.startsWith("invalid_")) {
                // Clear stored tokens before reloading to prevent stale token issues
                await userManager.removeUser();
                window.location.reload();
            } else {
                this.store.dispatch(
                    setBackendError({
                        code: "unknown",
                        details: typeof error === "string" ? error : JSON.stringify(error),
                    }),
                );
            }

            return;
        }
    }
}
